import { useMemo, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useBatteries,
  useBattery,
  useConfig,
  useDevelopmentConfig,
  useEvaluations,
  useOrg,
  usePlayerDevelopment,
  usePlayerFields,
  usePlayers,
  useSeasons,
  useSession,
  useTeams,
  useTrial,
  useTrials,
} from '@/app/queries';
import { useSession as useAuthSession } from '@/app/session';
import { formatDate, sheetName } from '@/lib/format';
import { resolveBatteryTests } from '@/lib/testing';
import { todayISO } from '@shared/dates';
import {
  evaluationFormReport,
  evaluationsReport,
  matchFormReport,
  playerReport,
  sessionFormReport,
  squadReport,
  testingFormReport,
  testingReport,
  trialReport,
} from '@/reports/build';
import { ReportView } from '@/reports/ReportView';
import { exportReportPdf, reportFilename } from '@/reports/export';
import type { BuildContext } from '@/reports/build';
import type { Report } from '@/reports/types';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput } from '@/ui/Field';
import { AiNarrative } from '@/routes/reports/AiNarrative';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { PaperPreview } from '@/ui/PaperPreview';

const KINDS = ['squad', 'player', 'development', 'testing', 'trial'] as const;

/**
 * Blank sheets to take to the pitch, as opposed to pages made from data.
 *
 * Kept apart in the picker because they answer a different question: not "what
 * do I hand somebody" but "what do I write on in the rain".
 */
const FORMS = ['sessionForm', 'matchForm', 'testingForm', 'evaluationForm'] as const;
type Kind = (typeof KINDS)[number] | (typeof FORMS)[number];

/**
 * Reports.
 *
 * One preview, one renderer, one document. What is on screen is what prints and
 * what downloads — a report that differs from its preview is a report nobody
 * trusts twice.
 */
export function Reports({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { user } = useAuthSession();
  const teams = useTeams();
  const org = useOrg();
  const config = useConfig();
  const fields = usePlayerFields();

  const [kind, setKind] = useState<Kind>('squad');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState('');
  const [batteryId, setBatteryId] = useState('');
  const [trialId, setTrialId] = useState('');
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const sheet = useRef<HTMLDivElement>(null);

  const teamList = teams.data ?? [];
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const players = usePlayers(activeTeamId ?? undefined);
  const seasons = useSeasons(activeTeamId ?? undefined);
  const evaluations = useEvaluations({ teamId: activeTeamId ?? undefined });
  const batteries = useBatteries(activeTeamId ?? undefined);
  const trials = useTrials(activeTeamId ?? undefined);
  const development = useDevelopmentConfig();
  const file = usePlayerDevelopment(kind === 'player' && playerId ? playerId : null);
  const battery = useBattery(
    (kind === 'testing' || kind === 'testingForm') && batteryId ? batteryId : null,
  );
  const trial = useTrial(kind === 'trial' && trialId ? trialId : null);

  const team = teamList.find((x) => x.id === activeTeamId);
  const season = (seasons.data ?? []).find((s) => s.status === 'active') ?? (seasons.data ?? [])[0];

  const ctx: BuildContext = useMemo(
    () => ({
      locale,
      t: t as BuildContext['t'],
      meta: {
        clubName: org.data?.name ?? '',
        teamName: team?.name,
        coachName: user?.name,
        seasonLabel: season?.label,
        onDate: formatDate(todayISO(), locale),
        logoUrl: org.data?.logoKey
          ? `/api/org/logo?v=${encodeURIComponent(org.data.logoKey)}`
          : null,
        accent: org.data?.primaryColor,
        footer: org.data?.reportFooter,
      },
    }),
    [locale, t, org.data, team?.name, user?.name, season?.label],
  );

  /*
    The tests this testing day is made of — the day's own list, not the club's
    catalogue. Both sheets read it, so the blank one a coach carries to the
    pitch has the same columns as the one he types up afterwards.
  */
  const batteryTests = useMemo(
    () =>
      battery.data
        ? resolveBatteryTests(
            battery.data.battery.testKeys,
            battery.data.tests,
            battery.data.results,
          )
        : [],
    [battery.data],
  );

  const report: Report | null = useMemo(() => {
    if (kind === 'squad') {
      return squadReport(
        ctx,
        players.data ?? [],
        config.data?.positions ?? [],
        fields.data ?? [],
        { includeSensitive },
      );
    }
    if (kind === 'sessionForm') return sessionFormReport(ctx);
    if (kind === 'matchForm') return matchFormReport(ctx);
    if (kind === 'testingForm') {
      return testingFormReport(
        ctx,
        battery.data?.battery ?? null,
        battery.data?.squad ?? players.data ?? [],
        batteryTests,
      );
    }
    if (kind === 'evaluationForm') return evaluationFormReport(ctx, players.data ?? []);
    if (kind === 'development') return evaluationsReport(ctx, evaluations.data ?? []);
    if (kind === 'player') return file.data ? playerReport(ctx, file.data) : null;
    if (kind === 'testing') {
      return battery.data
        ? testingReport(
            ctx,
            battery.data.battery,
            battery.data.squad,
            batteryTests,
            battery.data.results,
          )
        : null;
    }
    return trial.data
      ? trialReport(ctx, trial.data.trial, trial.data.candidates, trial.data.sessions)
      : null;
  }, [
    kind,
    ctx,
    players.data,
    config.data?.positions,
    fields.data,
    includeSensitive,
    evaluations.data,
    file.data,
    battery.data,
    batteryTests,
    trial.data,
    development.data,
  ]);

  const runExport = async () => {
    if (!sheet.current || !report) return;
    setExporting(true);
    setExportError(null);
    try {
      await exportReportPdf(
        sheet.current,
        reportFilename([report.meta.title, report.meta.subtitle], todayISO()),
      );
    } catch {
      // The browser can refuse this for reasons we cannot fix from here —
      // printing to PDF always works, so say so rather than failing silently.
      setExportError(t('report.failed'));
    } finally {
      setExporting(false);
    }
  };

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
        title={t('report.title')}
        lead={t('report.lead')}
        help={localePath(locale, '/guide/reports')}
      />
        <EmptyState title={t('report.nothing')} body={t('report.nothingBody')} />
      </>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('report.title')}
        lead={t('report.lead')}
        help={localePath(locale, '/guide/reports')}
      />

      <div className="grid gap-5">
        <Panel
          title={t('report.pick')}
          action={
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => window.print()}>
                {t('report.print')}
              </Button>
              <Button
                variant="primary"
                busy={exporting}
                disabled={!report}
                onClick={() => void runExport()}
              >
                {exporting ? t('report.preparing') : t('report.export')}
              </Button>
            </div>
          }
        >
          <div className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label={t('report.pick')}>
              {(p) => (
                <SelectInput
                  {...p}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as Kind)}
                >
                  <optgroup label={t('report.fromYourData')}>
                    {KINDS.map((k) => (
                      <option key={k} value={k}>
                        {t(`report.${k}`)}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label={t('report.formsGroup')}>
                    {FORMS.map((k) => (
                      <option key={k} value={k}>
                        {t(`report.${k}`)}
                      </option>
                    ))}
                  </optgroup>
                </SelectInput>
              )}
            </Field>

            {teamList.length > 1 ? (
              <Field label={t('nav.team')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={activeTeamId ?? ''}
                    onChange={(e) => setTeamId(e.target.value)}
                  >
                    {teamList.map((tm) => (
                      <option key={tm.id} value={tm.id}>
                        {tm.name}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>
            ) : null}

            {kind === 'player' ? (
              <Field label={t('evaluation.player')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={playerId}
                    onChange={(e) => setPlayerId(e.target.value)}
                  >
                    <option value="">{t('evaluation.pickPlayer')}</option>
                    {(players.data ?? []).map((pl) => (
                      <option key={pl.id} value={pl.id}>
                        {sheetName(pl.knownAs ?? pl.firstName, pl.lastName)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>
            ) : null}

            {kind === 'testing' || kind === 'testingForm' ? (
              <Field label={t('testing.title')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={batteryId}
                    onChange={(e) => setBatteryId(e.target.value)}
                  >
                    <option value="">—</option>
                    {(batteries.data ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} · {formatDate(b.onDate, locale)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>
            ) : null}

            {kind === 'trial' ? (
              <Field label={t('trial.title')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={trialId}
                    onChange={(e) => setTrialId(e.target.value)}
                  >
                    <option value="">—</option>
                    {(trials.data ?? []).map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name} · {formatDate(x.startsOn, locale)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>
            ) : null}

            {kind === 'squad' && (fields.data ?? []).some((f) => f.isSensitive) ? (
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="flex items-start gap-2 text-meta text-ink-muted">
                  <input
                    type="checkbox"
                    checked={includeSensitive}
                    onChange={(e) => setIncludeSensitive(e.target.checked)}
                    className="mt-0.5 size-4 accent-ink"
                  />
                  <span>
                    {t('report.includeSensitive')}
                    <span className="block text-ink-faint">{t('report.sensitiveWarning')}</span>
                  </span>
                </label>
              </div>
            ) : null}
          </div>

          {exportError ? (
            <div className="px-4 pb-4">
              <Notice tone="risk">{exportError}</Notice>
            </div>
          ) : null}
        </Panel>

        {report && activeTeamId ? (
          <Panel title={t('ai.narrative')}>
            <div className="p-4">
              {/* Given the figures the page prints and told it may use no
                  others, so the prose and the table cannot disagree. */}
              <AiNarrative
                teamId={activeTeamId}
                kind={kind}
                from={seasons.data?.[0]?.startsOn ?? todayISO()}
                to={todayISO()}
                figures={{
                  'players in the squad': players.data?.length ?? 0,
                  'evaluations recorded': evaluations.data?.length ?? 0,
                  season: seasons.data?.[0]?.label ?? null,
                  'report kind': kind,
                }}
              />
            </div>
          </Panel>
        ) : null}

        {report ? (
          /* A4 is 794px portrait and 1123px landscape. Scaled to fit, a coach can
             see the page he is about to hand over; the sheet keeps its
             millimetres, so what prints is unchanged (D-034). */
          <PaperPreview className="report-preview">
            <div ref={sheet} className="mx-auto w-fit shadow-(--shadow-sheet)">
              <ReportView report={report} />
            </div>
          </PaperPreview>
        ) : (
          <Panel title={t('report.title')}>
            <EmptyState title={t('report.nothing')} body={t('report.nothingBody')} />
          </Panel>
        )}
      </div>
    </div>
  );
}
